import React, { useState, useEffect } from 'react';
import { Upload, X, Plus, User, Building2, FileUp, ArrowLeft } from 'lucide-react';
import api from '../utils/api';
import * as XLSX from 'xlsx';


const EnhancedClientForm = ({ client, onSave, onCancel, onDelete, onImport, userSettings }) => {
  const [clientType, setClientType] = useState(client?.client_type || userSettings?.default_client_type || 'business');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(client?.logo_url || '');
  const [uploading, setUploading] = useState(false);
  const [csvMode, setCsvMode] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [csvMapping, setCsvMapping] = useState({});
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvError, setCsvError] = useState('');
  const [customFieldDefs, setCustomFieldDefs] = useState([]);
  
  // Extract primary contact if editing
  const primaryContact = client?.contacts?.find(c => c.is_primary) || client?.contacts?.[0] || {};
  
  // Form data state
  const [formData, setFormData] = useState({
    name: client?.name || '',
    client_type: clientType,
    email: client?.email || '',
    phone: client?.phone || '',
    company: client?.company || '',
    notes: client?.notes || '',
    
    // Business-specific fields
    website: client?.website || '',
    industry: client?.industry || '',
    logo_url: client?.logo_url || '',
    company_size: client?.company_size || '',
    founded_date: client?.founded_date || '',
    headquarters: client?.headquarters || '',
    description: client?.description || '',
    annual_revenue: client?.annual_revenue || '',
    
    // Contacts array for business clients
    contacts: client?.contacts || [],
  });

  // Primary contact state (required for all clients)
  const [primaryContactData, setPrimaryContactData] = useState({
    name: primaryContact?.name || '',
    email: primaryContact?.email || '',
    phone: primaryContact?.phone || '',
    job_title: primaryContact?.job_title || '',
    department: primaryContact?.department || ''
  });

  // Contact form state for adding new contacts
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContact, setNewContact] = useState({
    name: '',
    email: '',
    phone: '',
    job_title: '',
    department: '',
    is_primary: false
  });

  // Update form data when client type changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      client_type: clientType
    }));
  }, [clientType]);

  useEffect(() => {
    if (!csvMode) return;

    const loadDefinitions = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const [fieldsRes] = await Promise.all([
          api.get('/custom-field-definitions?entity_type=client', {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setCustomFieldDefs(Array.isArray(fieldsRes.data) ? fieldsRes.data : []);
      } catch (error) {
        console.error('Failed to load custom fields/values:', error);
      }
    };

    loadDefinitions();
  }, [csvMode]);

  const handleLogoUpload = async (file) => {
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    setUploading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await api.post(`/upload/logo`, formDataUpload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const logoUrl = response.data.file_url;
      setFormData(prev => ({ ...prev, logo_url: logoUrl }));
      setLogoPreview(logoUrl);
    } catch (error) {
      console.error('Failed to upload logo:', error);
      alert('Failed to upload logo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Upload immediately
      handleLogoUpload(file);
    }
  };

  const parseCsvFile = async (file) => {
    try {
      setCsvError('');
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' });

      if (!rows.length) {
        setCsvError('CSV appears to be empty.');
        return;
      }

      const headers = rows[0].map(h => String(h || '').trim());
      const dataRows = rows.slice(1).filter(row => row.some(cell => String(cell || '').trim() !== ''));

      if (!headers.length) {
        setCsvError('CSV header row is missing.');
        return;
      }

      setCsvHeaders(headers);
      setCsvRows(dataRows);

      const initialMapping = {};
      headers.forEach((header) => {
        const normalized = header.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

        const contactFieldMap = {
          contact_name: 'contact:name',
          contact: 'contact:name',
          contact_email: 'contact:email',
          contact_email_address: 'contact:email',
          contact_phone: 'contact:phone',
          contact_job_title: 'contact:job_title',
          contact_department: 'contact:department',
          first_name: 'contact:name',
          last_name: 'contact:name',
          full_name: 'contact:name'
        };

        const clientFieldMap = {
          client_name: 'field:name',
          company: 'field:name',
          company_name: 'field:name',
          business_name: 'field:name',
          organization: 'field:name',
          organization_name: 'field:name',
          client: 'field:name',
          name: 'field:name',
          email: 'field:email',
          phone: 'field:phone',
          website: 'field:website',
          industry: 'field:industry',
          address: 'field:address',
          notes: 'field:notes',
          description: 'field:description',
          headquarters: 'field:headquarters',
          founded_date: 'field:founded_date',
          annual_revenue: 'field:annual_revenue',
          company_size: 'field:company_size'
        };

        if (contactFieldMap[normalized]) {
          initialMapping[header] = contactFieldMap[normalized];
        } else if (clientFieldMap[normalized]) {
          initialMapping[header] = clientFieldMap[normalized];
        } else {
          initialMapping[header] = '';
        }
      });
      setCsvMapping(initialMapping);
    } catch (error) {
      console.error('CSV parse failed:', error);
      setCsvError('Failed to read CSV file. Please check the format.');
    }
  };

  const handleCsvFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFile(file);
    parseCsvFile(file);
  };

  const getMappedValue = (row, header) => {
    const index = csvHeaders.indexOf(header);
    return index >= 0 ? String(row[index] ?? '').trim() : '';
  };

  const buildImportPayloads = () => {
    const payloads = [];
    csvRows.forEach((row) => {
      const clientPayload = {
        name: '',
        email: '',
        phone: '',
        address: '',
        notes: '',
        client_type: userSettings?.default_client_type || 'business',
        company: '',
        website: '',
        industry: '',
        logo_url: '',
        company_size: '',
        founded_date: '',
        headquarters: '',
        description: '',
        annual_revenue: '',
        contacts: []
      };
      const contactPayload = {
        name: '',
        email: '',
        phone: '',
        job_title: '',
        department: ''
      };
      const customFieldValues = {};

      csvHeaders.forEach((header) => {
        const mapping = csvMapping[header];
        if (!mapping) return;
        const value = getMappedValue(row, header);
        if (value === '') return;

        if (mapping.startsWith('field:')) {
          const fieldKey = mapping.replace('field:', '');
          clientPayload[fieldKey] = value;
        } else if (mapping.startsWith('contact:')) {
          const contactKey = mapping.replace('contact:', '');
          contactPayload[contactKey] = value;
        } else if (mapping.startsWith('custom_field:')) {
          const fieldId = mapping.replace('custom_field:', '');
          customFieldValues[fieldId] = value;
        }
      });

      if (!clientPayload.name) {
        return;
      }

      if (!clientPayload.client_type) {
        clientPayload.client_type = userSettings?.default_client_type || 'business';
      }

      if (contactPayload.name || contactPayload.email || contactPayload.phone) {
        clientPayload.contacts = [{
          ...contactPayload,
          is_primary: true
        }];
      } else {
        clientPayload.contacts = [{
          name: clientPayload.name,
          email: clientPayload.email,
          phone: clientPayload.phone,
          is_primary: true
        }];
      }

      payloads.push({
        client: clientPayload,
        custom_fields: customFieldValues
      });
    });

    return payloads;
  };

  const handleCsvImport = async () => {
    if (!onImport) {
      setCsvError('CSV import handler not available.');
      return;
    }
    setCsvImporting(true);
    setCsvError('');

    try {
      const payloads = buildImportPayloads();
      if (!payloads.length) {
        setCsvError('No valid rows found. Ensure at least the client name is mapped.');
        return;
      }
      await onImport(payloads);
      setCsvImporting(false);
      setCsvMode(false);
    } catch (error) {
      console.error('CSV import failed:', error);
      setCsvError('Failed to import clients. Please review the file and try again.');
    } finally {
      setCsvImporting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    let contacts = [];
    
    if (clientType === 'business') {
      // For business clients, validate and use primary contact data
      if (!primaryContactData.name.trim()) {
        alert('Please enter a primary contact name');
        return;
      }
      
      // Validate and clean email
      const email = primaryContactData.email.trim();
      if (email && email.includes(' ')) {
        alert('Email address cannot contain spaces. Please check: ' + email);
        return;
      }
      
      contacts = [{
        ...primaryContactData,
        email: email, // Use trimmed email
        is_primary: true,
        id: Date.now().toString()
      }];
    } else {
      // For individual clients, validate and clean email
      const email = (formData.email || '').trim();
      if (email && email.includes(' ')) {
        alert('Email address cannot contain spaces. Please check: ' + email);
        return;
      }
      
      // For individual clients, use the client's own info as the primary contact
      contacts = [{
        name: formData.name,
        email: email, // Use trimmed email
        phone: formData.phone || '',
        job_title: '',
        department: '',
        is_primary: true,
        id: Date.now().toString()
      }];
    }
    
    // Clean the main formData email too
    const cleanFormData = {
      ...formData,
      email: (formData.email || '').trim(),
      contacts: contacts
    };
    
    console.log('📋 Form submission - clientDataToSave:', JSON.stringify(cleanFormData, null, 2));
    console.log('📇 Contacts at submission:', contacts);
    
    onSave(cleanFormData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            {csvMode ? (
              <button
                type="button"
                onClick={() => setCsvMode(false)}
                className="inline-flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to form</span>
              </button>
            ) : (
              <h2 className="text-xl font-semibold text-gray-900">
                {client ? 'Edit Client' : 'New Client'}
              </h2>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {!csvMode && !client && (
              <button
                type="button"
                onClick={() => setCsvMode(true)}
                className="inline-flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <FileUp className="w-4 h-4" />
                <span>Import CSV</span>
              </button>
            )}
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {csvMode ? (
          <div className="space-y-6">
            <div className="border border-dashed border-gray-300 rounded-xl p-6 text-center">
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleCsvFileChange}
                className="hidden"
                id="csv-upload-input"
              />
              <label htmlFor="csv-upload-input" className="cursor-pointer inline-flex flex-col items-center space-y-2">
                <Upload className="w-10 h-10 text-purple-500" />
                <span className="text-sm font-medium text-gray-700">
                  {csvFile ? csvFile.name : 'Upload a CSV file'}
                </span>
                <span className="text-xs text-gray-500">
                  Supports CSV, XLSX, or XLS files
                </span>
              </label>
            </div>

            {csvError && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3">
                {csvError}
              </div>
            )}

            {csvHeaders.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Map Columns</h3>
                <div className="grid grid-cols-1 gap-3">
                  {csvHeaders.map((header) => (
                    <div key={header} className="flex items-center justify-between border border-gray-200 rounded-lg p-3">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{header}</div>
                        <div className="text-xs text-gray-500">
                          Example: {csvRows[0] ? getMappedValue(csvRows[0], header) || '—' : '—'}
                        </div>
                      </div>
                      <select
                        value={csvMapping[header] || ''}
                        onChange={(e) => setCsvMapping({ ...csvMapping, [header]: e.target.value })}
                        className="border border-gray-300 rounded-md text-sm px-2 py-1 bg-white"
                      >
                        <option value="">Ignore</option>
                        <optgroup label="Client fields">
                          <option value="field:name">Client Name</option>
                          <option value="field:client_type">Client Type</option>
                          <option value="field:email">Client Email</option>
                          <option value="field:phone">Client Phone</option>
                          <option value="field:address">Address</option>
                          <option value="field:notes">Notes</option>
                          <option value="field:company">Company</option>
                          <option value="field:website">Website</option>
                          <option value="field:industry">Industry</option>
                          <option value="field:logo_url">Logo URL</option>
                          <option value="field:company_size">Company Size</option>
                          <option value="field:founded_date">Founded Date</option>
                          <option value="field:headquarters">Headquarters</option>
                          <option value="field:description">Description</option>
                          <option value="field:annual_revenue">Annual Revenue</option>
                        </optgroup>
                        <optgroup label="Primary contact">
                          <option value="contact:name">Contact Name</option>
                          <option value="contact:email">Contact Email</option>
                          <option value="contact:phone">Contact Phone</option>
                          <option value="contact:job_title">Contact Job Title</option>
                          <option value="contact:department">Contact Department</option>
                        </optgroup>
                        {customFieldDefs.length > 0 && (
                          <optgroup label="Custom client fields">
                            {customFieldDefs.map((field) => (
                              <option key={field.id} value={`custom_field:${field.id}`}>
                                {field.label || field.name}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setCsvMode(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCsvImport}
                    disabled={csvImporting}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-60"
                  >
                    {csvImporting ? 'Importing...' : `Import ${csvRows.length} Clients`}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Client Type Toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-3">Client Type</label>
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => setClientType('business')}
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg border transition-colors ${
                  clientType === 'business'
                    ? 'border-purple-500 bg-purple-50 text-purple-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Building2 className="w-5 h-5" />
                <span>Business</span>
              </button>
              <button
                type="button"
                onClick={() => setClientType('individual')}
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg border transition-colors ${
                  clientType === 'individual'
                    ? 'border-purple-500 bg-purple-50 text-purple-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <User className="w-5 h-5" />
                <span>Individual</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                Basic Information
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  {clientType === 'business' ? 'Business Name' : 'Full Name'} *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                  style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                  placeholder={clientType === 'business' ? 'Acme Corporation' : 'John Doe'}
                />
              </div>

              {clientType === 'individual' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                      style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                      placeholder="john@example.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                      style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">Company (Optional)</label>
                    <input
                      type="text"
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                      style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                      placeholder="Company they work for"
                    />
                  </div>
                </>
              )}

              {/* Business fields - distributed across both columns */}
              {clientType === 'business' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">Website</label>
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => setFormData({...formData, website: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                      style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                      placeholder="https://www.company.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">Industry</label>
                    <input
                      type="text"
                      value={formData.industry}
                      onChange={(e) => setFormData({...formData, industry: e.target.value})}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                      style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                      placeholder="Technology, Healthcare, Finance, etc."
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">Company Size</label>
                      <input
                        type="text"
                        value={formData.company_size}
                        onChange={(e) => setFormData({...formData, company_size: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                        style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                        placeholder="1-10, 11-50, etc."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">Founded</label>
                      <input
                        type="text"
                        value={formData.founded_date}
                        onChange={(e) => setFormData({...formData, founded_date: e.target.value})}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                        style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                        placeholder="2020"
                      />
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                  style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                  rows="3"
                  placeholder="Additional notes..."
                />
              </div>
            </div>

            {/* Business Details - Second Column */}
            {clientType === 'business' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
                  Additional Details
                </h3>

                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Company Logo</label>
                  <div className="flex items-center space-x-4">
                    {logoPreview && (
                      <img 
                        src={logoPreview} 
                        alt="Logo preview" 
                        className="w-16 h-16 object-contain border border-gray-200 rounded-lg"
                      />
                    )}
                    <div>
                      <input
                        type="file"
                        id="logo-upload"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                      <label
                        htmlFor="logo-upload"
                        className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <Upload className="w-4 h-4" />
                        <span>{uploading ? 'Uploading...' : 'Upload Logo'}</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Headquarters</label>
                  <input
                    type="text"
                    value={formData.headquarters}
                    onChange={(e) => setFormData({...formData, headquarters: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                    style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                    placeholder="New York, NY"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Annual Revenue</label>
                  <input
                    type="text"
                    value={formData.annual_revenue}
                    onChange={(e) => setFormData({...formData, annual_revenue: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                    style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                    placeholder="$1M-$10M, $10M+, etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                    style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                    rows="4"
                    placeholder="Brief description of the business..."
                  />
                </div>
              </div>
            )}
          </div>

          {/* Primary Contact Section (Required for Business Clients Only) */}
          {clientType === 'business' && (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2 mb-4">
                Primary Contact *
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Contact Name *</label>
                  <input
                    type="text"
                    required
                    value={primaryContactData.name}
                    onChange={(e) => setPrimaryContactData({...primaryContactData, name: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                    style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                    placeholder="John Smith"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Contact Email *</label>
                  <input
                    type="email"
                    required
                    value={primaryContactData.email}
                    onChange={(e) => setPrimaryContactData({...primaryContactData, email: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                    style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                    placeholder="john@company.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Contact Phone</label>
                  <input
                    type="tel"
                    value={primaryContactData.phone}
                    onChange={(e) => setPrimaryContactData({...primaryContactData, phone: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                    style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Job Title</label>
                  <input
                    type="text"
                    value={primaryContactData.job_title}
                    onChange={(e) => setPrimaryContactData({...primaryContactData, job_title: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                    style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                    placeholder="CEO, Manager, Director"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">Department</label>
                  <input
                    type="text"
                    value={primaryContactData.department}
                    onChange={(e) => setPrimaryContactData({...primaryContactData, department: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                    style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
                    placeholder="Sales, Engineering, Marketing"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex space-x-3 pt-6 border-t border-gray-200">
            {client && (
              <button
                type="button"
                onClick={() => {
                  onDelete(client.id);
                }}
                className="px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Delete Client
              </button>
            )}
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all font-medium"
            >
              {client ? 'Update Client' : 'Create Client'}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default EnhancedClientForm;
